import { lazy } from "react";

// project-imports
import MainLayout from "layout/MainLayout";
import Loadable from "components/Loadable";
import AuthGuard from "utils/route-guard/AuthGuard";
import PlanRoutes from "pages/Master/Plan/PlanRoutes"; // Import the PlanRoutes array
import TranslationRoutes from "pages/Master/Translation/TranslationRoutes";
import ProductRoutes from "pages/Master/Product/ProductRoutes";
import UserRoutes from "pages/Master/User/UserRoutes";
import EmailTemplateRoutes from "pages/Master/EmailTemplate/EmailTemplateRoutes";
import ProductTypeRoutes from "pages/Master/ProductType/ProductTypeRoutes";
import Error404 from "common/PageNotFound";
import VersionLicenceRoute from "pages/Master/VersionLicence/VersionLicenceRoutes";
import TransactionPageRoute from "pages/Transaction/TransactionPageRoutes";
import RedbirdRoutes from "pages/Master/Redbird/RedbirdRoutes";
import UserLicenceRoutes from "pages/Master/UserLicence/UserLicenceRoutes";
import AccountProfile from "pages/Settings/account";
import ModuleRoutes from "pages/Master/Module/ModuleRoutes";
import OrdersPageRoutes from "pages/Finance/Orders/OrdersPageRoutes";
import PayoutPageRoutes from "pages/Finance/Payout/PayoutListPageRoutes";
import RefundPageRoutes from "pages/Finance/Refund/RefundListPageRoutes";
import FailedOrderPageRoutes from "pages/Finance/Failed-Orders/FailedOrderListPageRoutes";
import TrafficRoutes from "pages/Traffic/TrafficRoutes";

// render - dashboard
const DashboardDefault = Loadable(
  lazy(() => import("pages/dashboard/default"))
);

// pages routing
const AuthLogin = Loadable(lazy(() => import("pages/Auth/Login/LoginPage")));
const VerifySteps = Loadable(lazy(() => import("pages/Auth/VerifySteps")));

const OrderPaymentPage = Loadable(
  lazy(() => import("pages/order-payment/OrderPaymentPage"))
);
const UserLogin = Loadable(lazy(() => import("pages/Auth/User/UserLoginPage")));
const EditProfilePage = Loadable(
  lazy(() => import("pages/Accounts/EditProfile/EditProfilePage"))
);
const UserProfile = Loadable(lazy(() => import("pages/Accounts/user")));
const ChangePasswordPage = Loadable(
  lazy(() => import("pages/Accounts/ChangePassword/ChangePasswordPage"))
);
const PlanDetailPage = Loadable(
  lazy(() => import("pages/Plan/PlanDetailPage"))
);
const ProductDetailsPage = Loadable(
  lazy(() => import("pages/Product/ProductDetailPage"))
);
const CheckoutPage = Loadable(
  lazy(() => import("pages/Checkout/CheckoutPage"))
);
const HistoryLicenceListPage = Loadable(
  lazy(() => import("pages/Master/HistoryLicence/historyLicenceListPage"))
);
const CheckOutPage = Loadable(
  lazy(() => import("pages/Checkout/OrderComplete"))
);
const AccountTabAccount = Loadable(
  lazy(() => import("pages/Settings/TabAccount"))
);
const AllUserListPage = Loadable(
  lazy(() => import("pages/Master/ListUsers/ListAllUsersPage"))
);
const PaymentPage = Loadable(
  lazy(() => import("pages/Accounts/Payment/PaymentPage"))
);
const PayoutPage = Loadable(
  lazy(() => import("pages/Finance/Payout-list/PayoutPage"))
);

const UserActivationPage = Loadable(
  lazy(() => import("pages/Licence-user/UserActivationPage"))
);

const ColudViewUserLogin = Loadable(
  lazy(() => import("pages/Auth/CloudView/CloudViewLoginPage"))
);

const UserDetailsPage = Loadable(
  lazy(() => import("pages/Cloud-View/User-Detail/UserDetailsPage"))
);

const SubscriptionListPage = Loadable(
  lazy(() => import("pages/Subscription/SubscriptionDetailPage"))
);

const UpdateCartPage = Loadable(
  lazy(() => import("pages/Licence-update/UpdateCartPage"))
);

const RenewDetailPage = Loadable(
  lazy(() => import("pages/Licence-Renew/RenewDetailPage"))
);

const ServerToolsPage = Loadable(
  lazy(() => import("pages/ServerTools/ServerToolsPage"))
);

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {
  path: "/",
  children: [
    {
      path: "/",
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "dashboard",
          element: <DashboardDefault />,
        },
        {
          // Include PlanRoutes under the path "/plan"
          path: "plan",
          children: [...PlanRoutes],
        },
        {
          // Include TranslationRoutes under the path "/translation"
          path: "translation",
          children: [...TranslationRoutes],
        },
        {
          // Include ProductRoutes under the path "/product"
          path: "product",
          children: [...ProductRoutes],
        },
        {
          // Include UserRoutes under the path "/user"
          path: "user",
          children: [...UserRoutes],
        },
        {
          // Include EmailTemplateRoutes under the path "/email-template"
          path: "email-template",
          children: [...EmailTemplateRoutes],
        },
        {
          // Include ProductTypeRoutes under the path "/product-type"
          path: "product-type",
          children: [...ProductTypeRoutes],
        },
        {
          path: "/products-detail/:product_id",
          element: <ProductDetailsPage />,
        },
        {
          path: "/plan-details/:plan_id",
          element: <PlanDetailPage />,
        },
        {
          path: "/cart",
          element: <CheckoutPage />,
        },
        {
          path: "/payout-list",
          element: <PayoutPage />,
        },
        {
          path: "user",
          element: <UserProfile />,
          children: [
            {
              path: "personal",
              element: <EditProfilePage />,
            },
            {
              path: "password",
              element: <ChangePasswordPage />,
            },
            {
              path: "payment",
              element: <PaymentPage />,
            },
          ],
        },
        {
          path: "account",
          element: <AccountProfile />,
          children: [
            {
              path: "setting",
              element: <AccountTabAccount />,
            },
          ],
        },
        {
          // Include VersionLicenceRoute under the path "/licence-version"
          path: "licence-version",
          children: [...VersionLicenceRoute],
        },
        {
          // Include HistoryLicenceListPage under the path "/licence-history"
          path: "/licence-history",
          element: <HistoryLicenceListPage />,
        },
        {
          path: "transaction",
          children: [...TransactionPageRoute],
        },
        {
          // Include ProductTypeRoutes under the path "/version-licence"
          path: "redbird",
          children: [...RedbirdRoutes],
        },
        {
          // Include ProductTypeRoutes under the path "/version-licence"
          path: "users-licence",
          children: [...UserLicenceRoutes],
        },
        {
          // Include AllUserListPage under the path "/all-users"
          path: "/all-users",
          element: <AllUserListPage />,
        },
        {
          path: "module",
          children: [...ModuleRoutes],
        },
        {
          path: "orders",
          children: [...OrdersPageRoutes],
        },
        {
          path: "payout",
          children: [...PayoutPageRoutes],
        },
        {
          path: "refund",
          children: [...RefundPageRoutes],
        },
        {
          path: "/user-details",
          element: <UserDetailsPage />,
        },
        {
          path: "subscription",
          element: <SubscriptionListPage />,
        },
        {
          path: "update-plan/:order_item_id",
          element: <UpdateCartPage />,
        },
        {
          path: "renew-plan/:order_item_id",
          element: <RenewDetailPage />,
        },
        {
          path: "failed-order",
          children: [...FailedOrderPageRoutes],
        },
        {
          // Include TrafficRoutes under the path "/traffic"
          path: "traffic",
          children: [...TrafficRoutes],
        },
        {
          path: "server-tools",
          element: <ServerToolsPage />,
        },
      ],
    },
    {
      path: "admin/login",
      element: <AuthLogin />,
    },
    {
      path: "login",
      element: <UserLogin />,
    },
    {
      path: "verify-step/:user_id",
      element: <VerifySteps />,
    },
    {
      path: "payment-link/:order_id",
      element: <OrderPaymentPage />,
    },
    {
      path: "checkout",
      element: <CheckOutPage />,
    },
    {
      path: "user-activation/:token",
      element: <UserActivationPage />,
    },
    {
      path: "cloud-view/login",
      element: <ColudViewUserLogin />,
    },
    {
      path: "*",
      element: <Error404 />,
    },
  ],
};

export default MainRoutes;
